import { LocalDate } from "@js-joda/core";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { type TimeReportsV2Filter } from "services/api/company/timeReports.service";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { formatSummaryDuration } from "web-apps/company/utils/timeAndDate";
import { TimeReportsSummaryCards } from "./components/common/summaryCards/timeReportsSummaryCards.component";
import { TimeReportsSummaryCard } from "./components/common/summaryCards/timeReportsSummaryCards.types";
import { useCompanyFilterOptions } from "web-apps/company/components/common/hooks/useCompanyFilterOptions";
import { TimeReportsOptionsPanel } from "./components/optionsPanel/timeReportsOptionsPanel.component";
import { PersonView } from "./components/views/person/personView.component";
import { TimeReportsToolbar } from "./components/toolbar/timeReportsToolbar.component";
import { Layout, PeriodSlot, SummaryStrip } from "./timeReports.styled";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import { useTimeReportsPreferencesSync } from "./hooks/useTimeReportsPreferencesSync";
import { buildTimeReportsSummaryCards } from "./timeReports.summary.mapper";

const toPositiveIntegerIds = (values: string[]): number[] => {
  const uniqueIds = new Set<number>();
  values.forEach((value) => {
    if (!/^\d+$/.test(value)) return;

    const parsedValue = Number(value);
    if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0) {
      return;
    }

    uniqueIds.add(parsedValue);
  });

  return Array.from(uniqueIds);
};

export const TimeReports = (): JSX.Element => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [timeReportsV2State, timeReportsV2Actions] = useTimeReportsV2Store();
  const companyId = companyState.company?.id;

  const [monthDate, setMonthDate] = useState<LocalDate>(LocalDate.now());
  const [activeSummaryFilter, setActiveSummaryFilter] =
    useState<TimeReportsV2Filter | null>(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [selectedWorkGroupIds, setSelectedWorkGroupIds] = useState<string[]>(
    [],
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [selectedRangeStart, setSelectedRangeStart] =
    useState<LocalDate | null>(null);
  const [selectedRangeEnd, setSelectedRangeEnd] = useState<LocalDate | null>(
    null,
  );
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);
  const filterOptions = useCompanyFilterOptions({
    companyId,
    setSelectedWorkGroupIds,
  });

  const workGroupIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedWorkGroupIds),
    [selectedWorkGroupIds],
  );
  const workerIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedPersonIds),
    [selectedPersonIds],
  );
  const activeFilters = useMemo<TimeReportsV2Filter[] | undefined>(
    () => (activeSummaryFilter == null ? undefined : [activeSummaryFilter]),
    [activeSummaryFilter],
  );
  const selectedDateRange = useMemo<{
    fromDate: LocalDate;
    toDate: LocalDate;
  } | null>(() => {
    if (!selectedRangeStart || !selectedRangeEnd) return null;
    if (selectedRangeStart.isAfter(selectedRangeEnd)) {
      return { fromDate: selectedRangeEnd, toDate: selectedRangeStart };
    }
    return { fromDate: selectedRangeStart, toDate: selectedRangeEnd };
  }, [selectedRangeEnd, selectedRangeStart]);
  const groupByOption = timeReportsV2State.preferences.groupByOption;
  const queryScopeKey = useMemo<string>(() => {
    const monthFromDate = monthDate.withDayOfMonth(1).toString();
    const workGroupKey = [...workGroupIds].sort((a, b) => a - b).join(",");
    const workerKey = [...workerIds].sort((a, b) => a - b).join(",");
    const filterKey = activeSummaryFilter ?? "";
    const selectedDateKey = selectedDateRange
      ? `${selectedDateRange.fromDate.toString()}-${selectedDateRange.toDate.toString()}`
      : "";
    return [
      monthFromDate,
      groupByOption,
      filterKey,
      workGroupKey,
      workerKey,
      selectedDateKey,
    ].join("|");
  }, [
    activeSummaryFilter,
    groupByOption,
    monthDate,
    selectedDateRange,
    workGroupIds,
    workerIds,
  ]);

  const isPreferencesHydrated = useTimeReportsPreferencesSync({
    companyId,
    preferences: timeReportsV2State.preferences,
    loadTimeReportsV2Preferences:
      timeReportsV2Actions.loadTimeReportsV2Preferences,
    persistTimeReportsV2Preferences:
      timeReportsV2Actions.persistTimeReportsV2Preferences,
  });

  useEffect(() => {
    setIsAllRowsExpanded(false);
  }, [queryScopeKey]);

  useEffect(() => {
    setSelectedRangeStart(null);
    setSelectedRangeEnd(null);
  }, [monthDate]);

  const handleExpandAllClick = useCallback((): void => {
    setIsAllRowsExpanded((value) => !value);
  }, []);

  const handleOptionsOpen = useCallback((): void => {
    setIsOptionsOpen(true);
  }, []);

  const handleOptionsClose = useCallback((): void => {
    setIsOptionsOpen(false);
  }, []);

  const handleSummaryCardClick = useCallback(
    (card: TimeReportsSummaryCard): void => {
      setActiveSummaryFilter((current) =>
        current === card.filter ? null : card.filter,
      );
    },
    [],
  );

  const handleRangeChange = useCallback((dates: LocalDate[]): void => {
    if (dates.length === 0) {
      setSelectedRangeStart(null);
      setSelectedRangeEnd(null);
      return;
    }

    const sortedDates = [...dates].sort((a, b) => a.compareTo(b));
    setSelectedRangeStart(sortedDates[0] ?? null);
    setSelectedRangeEnd(sortedDates[sortedDates.length - 1] ?? null);
  }, []);

  const summaryCards = useMemo<TimeReportsSummaryCard[]>(
    () =>
      buildTimeReportsSummaryCards({
        activeSummaryFilter,
        formatSummaryTime: formatSummaryDuration,
        summary: timeReportsV2State.summary,
        t,
      }),
    [activeSummaryFilter, t, timeReportsV2State.summary],
  );

  return (
    <Layout>
      <TimeReportsToolbar
        unitTeamOptions={filterOptions.unitTeamOptions}
        selectedWorkGroupIds={selectedWorkGroupIds}
        personOptions={filterOptions.personOptions}
        unitTeamEmptyText={
          filterOptions.unitTeamLoadError
            ? t("OrganizationalStructure.UnitTeamLoadFailed")
            : t("OrganizationalStructure.NoUnitTeamMatches")
        }
        personEmptyText={
          filterOptions.workersLoadError
            ? t("OrganizationalStructure.WorkersLoadFailed")
            : t("OrganizationalStructure.NoPersonMatches")
        }
        selectedPersonIds={selectedPersonIds}
        monthDate={monthDate}
        selectedRangeStart={selectedRangeStart}
        selectedRangeEnd={selectedRangeEnd}
        isAllRowsExpanded={isAllRowsExpanded}
        onSelectedWorkGroupIdsChange={setSelectedWorkGroupIds}
        onSelectedPersonIdsChange={setSelectedPersonIds}
        onDateRangeChange={handleRangeChange}
        onExpandAllClick={handleExpandAllClick}
        onOptionsClick={handleOptionsOpen}
      />
      <SummaryStrip>
        <PeriodSlot>
          <PeriodNavigator
            currentDate={monthDate}
            mode="month"
            onChange={setMonthDate}
            showSubLabel
            showYear
            showYearInMonthSubLabel={false}
            prevAriaLabel={t("TimeReports.PreviousPeriod")}
            nextAriaLabel={t("TimeReports.NextPeriod")}
          />
        </PeriodSlot>
        <TimeReportsSummaryCards
          cards={summaryCards}
          onCardClick={handleSummaryCardClick}
        />
      </SummaryStrip>
      <PersonView
        monthDate={monthDate}
        groupByOption={groupByOption}
        workGroupIds={workGroupIds}
        workerIds={workerIds}
        filters={activeFilters}
        dateRange={selectedDateRange}
        isAllRowsExpanded={isAllRowsExpanded}
        isPreferencesHydrated={isPreferencesHydrated}
        queryScopeKey={queryScopeKey}
      />
      <TimeReportsOptionsPanel
        isOpen={isOptionsOpen}
        groupByOption={groupByOption}
        onGroupByChange={timeReportsV2Actions.setGroupByOption}
        onClose={handleOptionsClose}
      />
    </Layout>
  );
};
